// pages/Ec2AppsView.jsx — EC2 Health → "Applications & services" view.
// Backend: GET /api/ec2-health/apps?account=…  POST /api/ec2-health/apps/scan?account=…  (ec2Apps.js)
// Inventory comes from a read-only probe (ec2AppsProbe.sh) over SSM or SSH, every 6 h + on demand.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../context/AuthContext';

export const CAT_STYLE = {
  SAP: 'bg-blue-50 text-blue-700 ring-blue-200',
  'Business app': 'bg-teal-50 text-teal-700 ring-teal-200',
  Database: 'bg-violet-50 text-violet-700 ring-violet-200',
  Web: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Runtime: 'bg-amber-50 text-amber-700 ring-amber-200',
  Container: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
  Mail: 'bg-pink-50 text-pink-700 ring-pink-200',
  Network: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  Storage: 'bg-orange-50 text-orange-700 ring-orange-200',
  'Custom service': 'bg-slate-100 text-slate-700 ring-slate-200',
  Agent: 'bg-gray-100 text-gray-600 ring-gray-200',
};
const CATS = Object.keys(CAT_STYLE);

export function ago(sqlTs) {
  if (!sqlTs) return 'never';
  const ms = Date.now() - new Date(String(sqlTs).replace(' ', 'T') + 'Z').getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
}
export const localTime = (sqlTs) => new Date(String(sqlTs).replace(' ', 'T') + 'Z').toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function StateBadge({ a }) {
  if (a.running) return <span className="rounded-full bg-emerald-100 px-2 py-px text-[11px] font-medium text-emerald-700">running</span>;
  return <span className="rounded-full bg-gray-100 px-2 py-px text-[11px] font-medium text-gray-500">{a.state || 'not running'}</span>;
}

function matches(inst, q) {
  if (!q) return true;
  const s = q.toLowerCase();
  const num = /^\d+$/.test(s) ? +s : null;
  return (inst.apps || []).some((a) => (a.label + ' ' + (a.detail || '')).toLowerCase().includes(s) || (num != null && (a.ports || []).includes(num)))
    || (inst.services || []).some((sv) => (sv.name + ' ' + (sv.desc || '')).toLowerCase().includes(s))
    || (inst.ports || []).some((p) => (num != null ? p.port === num : (p.proc || '').toLowerCase().includes(s)))
    || (inst.containers || []).some((c) => (c.name + ' ' + c.image).toLowerCase().includes(s))
    || (inst.windows && (inst.pkgs || []).some((p) => p.name.toLowerCase().includes(s)));
}

function hlFn(highlight) {
  return (txt) => {
    if (!highlight || !txt) return txt;
    const i = String(txt).toLowerCase().indexOf(highlight.toLowerCase());
    if (i < 0) return txt;
    return <>{txt.slice(0, i)}<mark className="rounded bg-yellow-100 px-0.5">{txt.slice(i, i + highlight.length)}</mark>{txt.slice(i + highlight.length)}</>;
  };
}

// Tabs that an instance's inventory can fill (used by the EC2 instance modal).
export function inventoryTabs(inst) {
  if (!inst) return [];
  const running = (inst.apps || []).filter((a) => a.running);
  return [
    ['apps', `Applications (${running.length})`],
    ['svc', `Services (${(inst.services || []).filter((s) => !s.system).length})`],
    ['ports', `Ports (${(inst.ports || []).length})`],
    ...(inst.containers && inst.containers.length ? [['ct', `Containers (${inst.containers.length})`]] : []),
    ...(inst.windows && inst.pkgs && inst.pkgs.length ? [['pkg', `Installed programs (${inst.pkgs.length})`]] : []),
    ...(inst.cron && inst.cron.length ? [['cron', `${inst.windows ? 'Scheduled tasks' : 'Cron'} (${inst.cron.length})`]] : []),
  ];
}

// Body of one inventory tab (apps / svc / ports / ct / pkg / cron).
export function InventoryTabBody({ inst, tab, highlight }) {
  const [sysSvc, setSysSvc] = useState(false);
  const running = (inst.apps || []).filter((a) => a.running);
  const idle = (inst.apps || []).filter((a) => !a.running);
  const appSvc = (inst.services || []).filter((s) => sysSvc || !s.system);
  const sysCount = (inst.services || []).filter((s) => s.system).length;
  const hl = hlFn(highlight);
  return (
    <>
      {tab === 'apps' && (
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
            <th className="py-1 pr-2 font-medium">Category</th><th className="py-1 pr-2 font-medium">Application</th>
            <th className="py-1 pr-2 font-medium">Status</th><th className="py-1 pr-2 font-medium">Details</th><th className="py-1 font-medium">Ports</th></tr></thead>
          <tbody>
            {[...running, ...idle].map((a) => (
              <tr key={a.key} className="border-b border-gray-100 align-top">
                <td className="py-1 pr-2 text-gray-500">{a.cat}</td>
                <td className="py-1 pr-2 font-medium text-gray-800">{hl(a.label)}</td>
                <td className="py-1 pr-2"><StateBadge a={a} /></td>
                <td className="max-w-[260px] py-1 pr-2 text-gray-600"><span className="break-words">{hl(a.detail) || '—'}</span></td>
                <td className="max-w-[220px] py-1 tabular-nums text-gray-600">
                  {a.ports && a.ports.length ? (a.ports.length > 8 ? `${a.ports.slice(0, 8).join(', ')} +${a.ports.length - 8}` : a.ports.join(', ')) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {tab === 'svc' && (
        <div>
          <label className="mb-2 flex items-center gap-1.5 text-[11px] text-gray-500">
            <input type="checkbox" checked={sysSvc} onChange={(e) => setSysSvc(e.target.checked)} /> Show OS/system services ({sysCount})
          </label>
          {inst.init && inst.init !== 'systemd' && inst.init !== 'windows' && <div className="mb-2 text-[11px] text-gray-400">This host uses SysV init: the list shows services enabled at boot (runlevel 3/5), not live state.</div>}
          <div className="grid gap-x-4 sm:grid-cols-2">
            {appSvc.map((s) => (
              <div key={s.name} className="flex items-baseline gap-2 border-b border-gray-100 py-1 text-xs">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${s.state === 'running' ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                <span className={`font-medium ${s.system ? 'text-gray-500' : 'text-gray-800'}`}>{hl(s.name)}</span>
                <span className="truncate text-gray-400" title={s.desc || ''}>{s.desc || (s.state === 'enabled' ? 'enabled at boot' : '')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === 'ports' && (
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
            <th className="py-1 pr-2 font-medium">Port</th><th className="py-1 pr-2 font-medium">Process</th><th className="py-1 pr-2 font-medium">Bound to</th><th className="py-1 font-medium">Application</th></tr></thead>
          <tbody>
            {(inst.ports || []).map((p) => {
              const owner = (inst.apps || []).find((a) => (a.ports || []).includes(p.port));
              return (
                <tr key={p.port + (p.proc || '')} className="border-b border-gray-100">
                  <td className="py-1 pr-2 font-medium tabular-nums text-gray-800">{p.port}</td>
                  <td className="py-1 pr-2 text-gray-700">{hl(p.proc) || '—'}</td>
                  <td className="py-1 pr-2">
                    <span className={p.scope === 'all' ? 'text-amber-700' : p.scope === 'local' ? 'text-gray-400' : 'text-gray-600'}>
                      {p.scope === 'all' ? 'all interfaces' : p.scope === 'local' ? 'localhost only' : p.addr}</span>
                  </td>
                  <td className="py-1 text-gray-500">{owner ? owner.label : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {tab === 'ct' && (
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
            <th className="py-1 pr-2 font-medium">Container</th><th className="py-1 pr-2 font-medium">Image</th><th className="py-1 pr-2 font-medium">Status</th><th className="py-1 font-medium">Published ports</th></tr></thead>
          <tbody>
            {inst.containers.map((c) => (
              <tr key={c.name} className="border-b border-gray-100 align-top">
                <td className="py-1 pr-2 font-medium text-gray-800">{hl(c.name)}</td>
                <td className="py-1 pr-2 text-gray-600">{hl(c.image)}</td>
                <td className="py-1 pr-2 text-gray-600">{c.status}</td>
                <td className="py-1 text-gray-500">{(c.ports || '').replace(/, \[::\][^,]*/g, '') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {tab === 'pkg' && (
        <div>
          {inst.features && inst.features.length > 0 && (
            <div className="mb-2 text-[11px] text-gray-500">Windows roles: {inst.features.map((f) => f.label).join(', ')}</div>
          )}
          <table className="w-full text-xs">
            <thead><tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <th className="py-1 pr-2 font-medium">Program</th><th className="py-1 pr-2 font-medium">Version</th><th className="py-1 font-medium">Publisher</th></tr></thead>
            <tbody>
              {inst.pkgs.map((p) => (
                <tr key={p.name + p.version} className="border-b border-gray-100">
                  <td className="py-1 pr-2 text-gray-800">{hl(p.name)}</td>
                  <td className="py-1 pr-2 tabular-nums text-gray-600">{p.version || '—'}</td>
                  <td className="py-1 text-gray-500">{p.publisher || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === 'cron' && (
        <pre className="max-h-64 overflow-auto rounded-lg bg-white p-2 text-[11px] leading-relaxed text-gray-700 ring-1 ring-gray-200">{inst.cron.join('\n')}</pre>
      )}
    </>
  );
}

function InstanceApps({ inst, onOpen, highlight }) {
  const running = (inst.apps || []).filter((a) => a.running);
  const hl = hlFn(highlight);
  return (
    <div id={`ec2apps-${inst.id}`} className={`rounded-xl border bg-white shadow-sm transition hover:shadow-md ${inst.findings && inst.findings.length ? 'border-amber-200' : 'border-gray-200'}`}>
      <button type="button" onClick={onOpen} className="w-full p-4 text-left">
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-gray-900">{inst.name}</div>
            <div className="truncate text-[11px] text-gray-500">
              {inst.osName || '—'}{inst.pkgCount ? ` · ${inst.pkgCount} packages` : ''}
              {inst.source ? ` · via ${inst.source.toUpperCase()}` : ''} · scanned {ago(inst.scannedAt)}
            </div>
          </div>
          <span className="text-xs text-sky-600">Open</span>
        </div>
        {inst.error && !running.length && <div className="mt-2 rounded-md bg-gray-50 px-2 py-1.5 text-xs text-gray-500">{inst.error}</div>}
        {inst.error && running.length > 0 && <div className="mt-2 text-[11px] text-amber-700">Last scan failed ({inst.error}) — showing the previous inventory.</div>}
        {running.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {running.filter((a) => a.cat !== 'Agent').map((a) => (
              <span key={a.key} className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ${CAT_STYLE[a.cat] || CAT_STYLE.Agent}`} title={a.detail || ''}>{hl(a.label)}</span>
            ))}
          </div>
        )}
        {inst.findings && inst.findings.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {inst.findings.map((f) => <li key={f} className="flex gap-1.5 text-xs text-amber-700"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{f}</li>)}
          </ul>
        )}
      </button>
    </div>
  );
}

export default function Ec2AppsView({ account, scopeIds, onOpenInstance }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [q, setQ] = useState('');
  const [appF, setAppF] = useState(null);
  const [chgDays, setChgDays] = useState(30);

  const load = useCallback(() => {
    setErr(null);
    return api.get(`/api/ec2-health/apps?account=${account}&changeDays=${chgDays}`).then((r) => setData(r.data))
      .catch((e) => setErr((e.response && e.response.data && e.response.data.error) || e.message));
  }, [account, chgDays]);
  useEffect(() => { load(); }, [load]);

  const scanNow = async () => {
    setScanning(true);
    try { await api.post(`/api/ec2-health/apps/scan?account=${account}`); await load(); }
    catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    finally { setScanning(false); }
  };

  const insts = useMemo(() => {
    if (!data) return [];
    return data.instances
      .filter((i) => !scopeIds || scopeIds.has(i.id))
      .filter((i) => !appF || (i.apps || []).some((a) => a.running && a.label === appF))
      .filter((i) => matches(i, q.trim()))
      .sort((a, b) => (b.apps || []).length - (a.apps || []).length || a.name.localeCompare(b.name));
  }, [data, scopeIds, appF, q]);

  const index = useMemo(() => {
    if (!data) return [];
    const ids = new Set(data.instances.filter((i) => !scopeIds || scopeIds.has(i.id)).map((i) => i.id));
    return data.appIndex.map((a) => Object.assign({}, a, { n: a.instances.filter((id) => ids.has(id)).length }))
      .filter((a) => a.n && a.cat !== 'Agent' && a.cat !== 'Custom service')
      .sort((a, b) => CATS.indexOf(a.cat) - CATS.indexOf(b.cat) || b.n - a.n || a.label.localeCompare(b.label));
  }, [data, scopeIds]);

  if (err && !data) return <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>;
  if (!data) return <div className="p-8 text-center text-sm text-gray-400">Loading inventory…</div>;

  const never = !data.lastScan;
  const findings = insts.flatMap((i) => (i.findings || []).map((f) => ({ name: i.name, f })));
  const changes = data.changes.filter((c) => !scopeIds || scopeIds.has(c.instanceId));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find app, service, process or port (e.g. nginx, 3306)…"
          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm sm:w-80" />
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          <span>Last scan {ago(data.lastScan)} · every 6 h</span>
          <button type="button" onClick={scanNow} disabled={scanning}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {scanning ? 'Scanning… (≈10–40 s)' : 'Scan now'}</button>
        </div>
      </div>

      {never && <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">No inventory yet — click <b>Scan now</b>.</div>}

      {index.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Running applications — click to see where</div>
          <div className="flex flex-wrap gap-1.5">
            {index.map((a) => (
              <button key={a.label} type="button" onClick={() => setAppF(appF === a.label ? null : a.label)}
                className={`rounded-md px-2 py-1 text-xs font-medium ring-1 transition ${CAT_STYLE[a.cat] || CAT_STYLE.Agent} ${appF === a.label ? 'outline outline-2 outline-sky-400' : appF ? 'opacity-50' : ''}`}>
                {a.label} <span className="ml-0.5 tabular-nums opacity-70">{a.n}</span>
              </button>
            ))}
          </div>
          {appF && <button type="button" onClick={() => setAppF(null)} className="mt-2 text-xs text-sky-600 hover:underline">Clear “{appF}”</button>}
        </div>
      )}

      {findings.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <div className="font-semibold">Exposure findings</div>
          <ul className="mt-1 space-y-0.5 text-xs">{findings.map((x, i) => <li key={i}><b>{x.name}</b>: {x.f}</li>)}</ul>
        </div>
      )}

      <div className="grid items-start gap-3 lg:grid-cols-2">
        {insts.map((i) => (
          <InstanceApps key={i.id} inst={i} highlight={q.trim()} onOpen={() => onOpenInstance && onOpenInstance(i.id, 'apps')} />
        ))}
      </div>
      {!insts.length && !never && <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">No instances match.</div>}

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-800">What changed</h3>
          <span className="text-[11px] text-gray-400">apps, services, ports and containers added or removed between scans</span>
          <select value={chgDays} onChange={(e) => setChgDays(+e.target.value)} className="ml-auto rounded-md border border-gray-200 px-2 py-1 text-xs">
            {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
        {changes.length ? (
          <table className="w-full text-xs">
            <tbody>
              {changes.slice(0, 150).map((c, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="whitespace-nowrap py-1 pr-2 text-gray-400">{localTime(c.ts)}</td>
                  <td className="py-1 pr-2 text-gray-800">{c.name}</td>
                  <td className="py-1 pr-2 text-gray-500">{c.kind}</td>
                  <td className="py-1 pr-2">
                    <span className={`rounded px-1.5 py-px text-[11px] font-medium ${c.change === 'added' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{c.change}</span>
                  </td>
                  <td className="py-1 text-gray-700">{c.item}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="text-xs text-gray-400">No changes recorded{never ? '' : ' — the first scan is the baseline; differences appear from the next scan onward'}.</div>}
      </section>

      <div className="text-[11px] leading-relaxed text-gray-400">
        Read-only scan over SSM Run Command or SSH: running services, listening ports with their process, top processes, Docker containers,
        /usr/sap and /sybase installs, key packages and cron entries. Windows (PowerShell via SSM): running services, ports, installed programs, roles and scheduled tasks; needs the SSM agent online.
      </div>
    </div>
  );
}
